import { ConductorOneChildActive, Router, ScreenBase, HashNavigationAdapter } from "@frui.ts/screens";
import { interfaces } from "inversify";
import { computed } from "mobx";
import LocaleProvider from "services/localeProvider";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import AppRouter from "services/appRouter";
import AlertViewModel, { AlertVariant } from "./alertViewModel";
import ChargeManagementViewModel, { ChargeManagementModuleNavigationName } from "./chargeManagement/chargeManagementViewModel";
import EshopViewModel from "./eshop/eshopViewModel";
import MultiEshopViewModel from "./multiEshop/multiEshopViewModel";
import ExemptionNoticeViewModel from "./notice/exemption/exemptionNoticeViewModel";
import LicensePlateChangeNoticeViewModel from "./notice/licensePlateChange/licensePlateChangeNoticeViewModel";
import OmissionNoticeViewModel from "./notice/omission/omissionNoticeViewModel";
import PaymentValidationViewModel from "./paymentValidation/paymentValidationViewModel";
import TradingPlacesViewModel from "./tradingPlaces/tradingPlacesViewModel";
import OutagesWidgetViewModel from "./outages/outagesWidgetViewModel";
import RefundRequestNoticeViewModel from "./notice/refundRequest/refundRequestNoticeViewModel";
import PriceListViewModel from "./common/priceListViewModel";
import CustomerReviewViewModel from "./customerReview/customerReviewViewModel";

export const ChargeManagementModuleDeeplink = "charge/charge_management_validation";

@Router.registerRoute({
  route: "",
  children: [
    EshopViewModel,
    MultiEshopViewModel,
    ExemptionNoticeViewModel,
    OmissionNoticeViewModel,
    RefundRequestNoticeViewModel,
    LicensePlateChangeNoticeViewModel,
    PaymentValidationViewModel,
    ChargeManagementViewModel,
    TradingPlacesViewModel,
    PriceListViewModel,
    CustomerReviewViewModel,
  ],
})
export default class RootViewModel extends ConductorOneChildActive<ScreenBase> {
  private urlAdapter: HashNavigationAdapter;

  constructor(
    private localeProvider: LocaleProvider,
    public localizationService: LocalizationService,
    private notificationService: NotificationService,
    private appRouter: AppRouter,
    children: ScreenBase[]
  ) {
    super();
    this.urlAdapter = new HashNavigationAdapter();
    this.appRouter.start(this);
    children.forEach(child => this.children.push(child));
  }

  static Factory({ container }: interfaces.Context) {
    return (children: interfaces.ServiceIdentifier<ScreenBase>[]) =>
      new RootViewModel(
        container.get(LocaleProvider),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(AppRouter),
        children.map(child => container.get(child))
      );
  }

  protected async onInitialize() {
    await this.urlAdapter.start(this);
    if (!this.activeChild && this.children.length) {
      return this.tryActivateChild(this.children[0]);
    }
  }

  @computed get language() {
    return this.localeProvider.locale;
  }

  set language(locale: string) {
    this.localeProvider.changeLocale(locale);
  }

  @computed
  get languages() {
    return this.localeProvider.locales.map((locale: string) => ({
      id: locale,
      label: this.localizationService.translateGeneral(`languages.${locale}`),
    }));
  }

  navigate(subPath: string | undefined, params: any): Promise<void> {
    // Support for charge management QR code deeplink
    if (subPath === ChargeManagementModuleDeeplink) {
      subPath = ChargeManagementModuleNavigationName;
    }

    return super.navigate(subPath, params);
  }

  @computed
  get alerts() {
    const notificationTypeToAlertVariant = (type: NotificationType) => {
      return {
        [NotificationType.ERROR]: AlertVariant.DANGER,
        [NotificationType.INFO]: AlertVariant.INFO,
        [NotificationType.SUCCESS]: AlertVariant.SUCCESS,
        [NotificationType.WARNING]: AlertVariant.WARNING,
      }[type];
    };

    // Don´t show alerts for outage widget
    if (this.activeChild instanceof OutagesWidgetViewModel) {
      return [];
    }

    const items: AlertViewModel[] = [];

    this.notificationService.notifications.forEach((notification, key) => {
      let onClose;

      if (!notification.permanent) {
        onClose = () => {
          this.notificationService.remove(key);
        };
      }
      items.push(new AlertViewModel(notification, notificationTypeToAlertVariant(notification.type), onClose));
    });

    return items;
  }
}
